import React, { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { bapi } from '@/lib/billingApi';
import { Segmented } from '../Segmented';
import { COUPON_PLANS } from './couponText';

const EMPTY = { code: '', type: 'trial', trial_days: '30', percent_off: '20', applies_to: [], duration: 'once', max_uses: '100' };

const TrialFields = ({ f, set }) => (
  <div>
    <Label>Free Pro days</Label>
    <Select value={f.trial_days} onValueChange={(v) => set({ trial_days: v })}>
      <SelectTrigger className="mt-1" data-testid="trial-days-select"><SelectValue /></SelectTrigger>
      <SelectContent>
        {['30', '90', '180'].map((d) => <SelectItem key={d} value={d}>{d} Days</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const PercentFields = ({ f, set }) => {
  const toggle = (id, on) => set({ applies_to: on ? [...f.applies_to, id] : f.applies_to.filter((p) => p !== id) });
  return (
    <>
      <div>
        <Label htmlFor="coupon-percent">Percent off</Label>
        <div className="relative mt-1">
          <Input id="coupon-percent" type="number" value={f.percent_off} onChange={(e) => set({ percent_off: e.target.value })} className="pr-8" data-testid="coupon-percent-input" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
        </div>
      </div>
      <div>
        <Label>Applies to</Label>
        <div className="mt-1.5 space-y-2">
          {COUPON_PLANS.map(([id, label]) => (
            <label key={id} htmlFor={`coupon-plan-${id}`} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox id={`coupon-plan-${id}`} checked={f.applies_to.includes(id)} onCheckedChange={(v) => toggle(id, !!v)} data-testid={`coupon-plan-${id}`} />{label}
            </label>
          ))}
        </div>
      </div>
      <div>
        <Label>Discount lasts for</Label>
        <Segmented value={f.duration} onChange={(v) => set({ duration: v })} testId="coupon-duration" options={[['once', 'First payment only'], ['forever', 'Every renewal']]} />
      </div>
      <p className="text-xs text-slate-500">Percent-off coupons work only when paying by card.</p>
    </>
  );
};

const bodyFor = (f) => ({
  code: f.code.trim().toUpperCase(), type: f.type, max_uses: parseInt(f.max_uses, 10),
  ...(f.type === 'percent' ? { percent_off: parseInt(f.percent_off, 10), applies_to: f.applies_to, duration: f.duration } : { trial_days: parseInt(f.trial_days, 10) }),
});

export const CreateCouponForm = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.code.trim()) { toast.error('Coupon code is required'); return; }
    if (f.type === 'percent') {
      const pct = Number(f.percent_off);
      if (!Number.isInteger(pct) || pct < 1 || pct > 99) { toast.error('Percent off must be a whole number from 1 to 99'); return; }
      if (!f.applies_to.length) { toast.error('Pick at least one plan the coupon applies to'); return; }
    }
    setBusy(true);
    try {
      await bapi('/admin/coupons', { method: 'POST', body: bodyFor(f) });
      toast.success('Coupon created successfully!');
      setF(EMPTY);
      onCreated();
    } catch (err) { toast.error(err.message || 'Failed to create coupon'); } finally { setBusy(false); }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Plus className="w-5 h-5" />Create Coupon</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4" data-testid="create-coupon-form">
          <div>
            <Label htmlFor="new-coupon-code">Coupon Code</Label>
            <Input id="new-coupon-code" placeholder="e.g., SPRING2026" value={f.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} className="mt-1" data-testid="new-coupon-code" />
          </div>
          <div>
            <Label>Type</Label>
            <Segmented value={f.type} onChange={(v) => set({ type: v })} testId="coupon-type" options={[['trial', 'Free Pro days'], ['percent', 'Percent off']]} />
          </div>
          {f.type === 'percent' ? <PercentFields f={f} set={set} /> : <TrialFields f={f} set={set} />}
          <div>
            <Label htmlFor="coupon-max-uses">Max Uses</Label>
            <Input id="coupon-max-uses" type="number" value={f.max_uses} onChange={(e) => set({ max_uses: e.target.value })} className="mt-1" data-testid="max-uses-input" />
          </div>
          <Button type="submit" className="w-full bg-[#009E49] hover:bg-[#008840]" disabled={busy} data-testid="create-coupon-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Coupon'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
